/**
 * Pure geometry for the directions view's map cut-outs: which part of a plan a step shows, and which rooms fall in
 * it. Kept free of React and the DOM so it is unit-tested (geometry.test.ts).
 */
import type { NavigateInstruction, NavigationSegment } from "@/lib/api/client";

export type Point = [number, number];

/** An axis-aligned rectangle in plan (SVG user) units. */
export interface Rect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export function boundsOf(points: readonly (readonly number[])[]): { minX: number; minY: number; maxX: number; maxY: number } | null {
  if (!points.length) return null;
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (const [x, y] of points) {
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (y < minY) minY = y;
    if (y > maxY) maxY = y;
  }
  return { minX, minY, maxX, maxY };
}

/** A plan's own viewBox ("0 0 3578.67 708.66") from its SVG's root tag. */
export function parseViewBox(svg: string): Rect | null {
  const m = svg.match(/<svg[^>]*\bviewBox=["']([^"']+)["']/i);
  if (!m) return null;
  const [x, y, w, h] = m[1].trim().split(/[\s,]+/).map(Number);
  if (![x, y, w, h].every(Number.isFinite) || w <= 0 || h <= 0) return null;
  return { x, y, w, h };
}

/** The points a step's map is about: the stretch it describes, else the spot it points at. */
export function stepPoints(inst: Pick<NavigateInstruction, "path_portion" | "focus">): Point[] | null {
  if (inst.path_portion?.length) return inst.path_portion;
  if (inst.focus) return [inst.focus];
  return null;
}

/**
 * The part of the plan a step's cut-out shows (before it is fitted to the box). The same framing the cut-out had
 * as a zoomed viewer: a short stretch or a single spot shows a 600×400 window around it, kept to the route's
 * neighbourhood (`fullPath`, 50 units of margin) and padded 40 units; anything longer is its own bounds padded 80.
 */
export function cutoutRect(points: readonly Point[], fullPath?: readonly Point[]): Rect | null {
  const b = boundsOf(points);
  if (!b) return null;
  const { minX, minY, maxX, maxY } = b;
  let rect: [number, number, number, number];
  let pad: number;
  const all = fullPath && fullPath.length >= 2 ? boundsOf(fullPath) : null;
  if (all && Math.max(maxX - minX, maxY - minY) < 200) {
    const cx = (minX + maxX) / 2, cy = (minY + maxY) / 2;
    rect = [
      Math.max(cx - 300, all.minX - 50),
      Math.max(cy - 200, all.minY - 50),
      Math.min(cx + 300, all.maxX + 50),
      Math.min(cy + 200, all.maxY + 50),
    ];
    pad = 40;
  } else {
    rect = [minX, minY, maxX, maxY];
    pad = 80;
  }
  return { x: rect[0] - pad, y: rect[1] - pad, w: rect[2] - rect[0] + pad * 2, h: rect[3] - rect[1] + pad * 2 };
}

/**
 * What a box shows of `rect` once fitted (SVG `xMidYMid meet`): the rect grows along its short side, about its
 * centre, until it has the box's aspect. `aspect` = box width / height.
 */
export function fitToAspect(rect: Rect, aspect: number): Rect {
  if (!(aspect > 0)) return rect;
  const cx = rect.x + rect.w / 2, cy = rect.y + rect.h / 2;
  let { w, h } = rect;
  if (w / h < aspect) w = h * aspect;
  else h = w / aspect;
  return { x: cx - w / 2, y: cy - h / 2, w, h };
}

/** Everything a box of any aspect in [minAspect, maxAspect] can show of `rect`: the widest fit's span across, the
 *  tallest fit's span down. */
export function viewForAspects(rect: Rect, minAspect: number, maxAspect: number): Rect {
  const wide = fitToAspect(rect, maxAspect);
  const tall = fitToAspect(rect, minAspect);
  return { x: wide.x, y: tall.y, w: wide.w, h: tall.h };
}

export function intersects(a: Rect, b: Rect): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

/** The items whose outline's bounds fall at least partly in `view`; items without an outline are left out. */
export function itemsInView<T>(items: readonly T[], outline: (item: T) => readonly (readonly number[])[] | null | undefined, view: Rect): T[] {
  return items.filter((item) => {
    const pts = outline(item);
    const b = pts?.length ? boundsOf(pts) : null;
    return !!b && intersects(view, { x: b.minX, y: b.minY, w: b.maxX - b.minX, h: b.maxY - b.minY });
  });
}

/** Every walk leg's path on each plan, joined: a step's cut-out draws its whole floor's route faintly. */
export function routeByFloorplan(segments: readonly NavigationSegment[]): Map<number, Point[]> {
  const map = new Map<number, Point[]>();
  for (const seg of segments) {
    if (seg.type !== "walk") continue;
    map.set(seg.floorplan_id, [...(map.get(seg.floorplan_id) ?? []), ...seg.path]);
  }
  return map;
}
